/**
 * Enrollment error message mappings
 * Maps backend error codes to user-friendly messages
 */

// All error messages in one map (codes are unique across flows)
export const ERROR_MESSAGES = {
  // Direct Join (Verify.vue) - email/PIN verification
  // Note: email_domain_restricted uses backend's formatted message (includes allowed patterns)
  'recaptcha_failed': 'reCAPTCHA verification failed. Please try again or contact TWA for support.',
  'verification_code_invalid': 'The code given is not correct. Please check your email and try again, or contact TWA for support.',

  // LTI 1.1 processing (Pay.vue) - TWA-hosted enrollment
  'canvas_user_creation_failed': 'Unable to create your account. Please contact TWA for support.',
  'enrollment_save_failed': 'Unable to complete enrollment. Please contact TWA for support.',
  'passback_failed': 'Unable to complete enrollment. Please contact TWA for support.',
  'course_not_found': 'Course not found. Please check your link or contact TWA for support.',
  'enrollment_not_found': 'Enrollment not found. Please try enrolling again or contact TWA for support.',
  'membership_not_found': 'Enrollment not found. Please try enrolling again or contact TWA for support.',

  // LTI 1.3 processing (lti/Pay.vue) - school-hosted enrollment
  'grade_submission_failed': 'Unable to complete enrollment. Please contact TWA for support.',
  'oauth_failed': 'Unable to complete enrollment. Please contact TWA for support.',

  // Infrastructure errors
  'database_unavailable': 'Please try again or contact TWA for support.',
  'canvas_api_error': 'Please try again or contact TWA for support.',
  'network_error': 'Please try again or contact TWA for support.',
  'stripe_api_error': 'Please try again or contact TWA for support.',
  'unknown_enrollment_type': 'Unable to process enrollment. Please contact TWA for support.',

  'course_not_published': 'This course link is not available. Please contact TWA for support.',
  'course_not_activated': 'This course link is not available. Please contact TWA for support.',
  'not_twa_hosted': 'This course link is not available. Please contact TWA for support.',
  'join_link_expired': 'This course link has expired. Please contact TWA for support.',

  // Coupon/access code errors (used in both LTI 1.1 and 1.3)
  'access_code_inactive': "This access code is no longer active. Please return it and ask for a new access code.",
  'access_code_max_usage': "This access code has run out of uses. Please let your school/organization know.",
  'access_code_used_other_course': "You've already used this access code for another course. Please purchase a new one, or contact TWA if you switched courses.",
  'access_code_used_by_other': "This access code has already been used by someone else. Please return it and ask for a new access code.",
  'access_code_not_found': "This access code is not recognized. Please check for typos.",
  'access_code_expired': "This access code has expired. Please contact your school/organization.",
  'access_code_product_mismatch': "This access code doesn't match the units in this course.",
  // TWA-322: server-side refusal when the school has access codes switched off.
  // Only reachable from a stale bundle or a hand-crafted request — current bundles
  // hide the access-code form when accepts_access_code is off.
  'access_code_not_accepted': "Access codes aren't used for this course. Please ask your teacher how to enroll, or contact TWA for support."
}

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please contact TWA for support.'

/**
 * Get user-friendly message for an error code
 * Used by both Beacon and student display
 *
 * @param {string} code - Error code from backend
 * @returns {string} User-friendly error message
 */
export function getMessageForCode(code) {
  return ERROR_MESSAGES[code] || DEFAULT_ERROR_MESSAGE
}

/**
 * Extract a user-friendly error message from an API error
 * Provides consistent error handling across all enrollment flows
 *
 * @param {Error} error - Axios error object
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
  // 1. Backend error code with a known mapping. An UNMAPPED code deliberately
  //    falls through to the backend's own message below (e.g.
  //    email_domain_restricted's formatted allowed-domains message) instead of
  //    collapsing to the generic default.
  const errorCode = error.response?.data?.error?.code
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode]
  }

  // 2. Check for nested error.message (specific validation messages)
  const nestedMessage = error.response?.data?.error?.message
  if (nestedMessage && nestedMessage !== 'Bad Request') {
    return nestedMessage
  }

  // 3. Check for top-level message in response data
  const dataMessage = error.response?.data?.message
  if (dataMessage && dataMessage !== 'Bad Request') {
    return dataMessage
  }

  // 4. Handle network errors
  if (error.message === 'Network Error') {
    return 'Network error. Please check your connection and try again.'
  }

  // 5. Handle server errors (500+)
  if (error.response?.status >= 500) {
    return DEFAULT_ERROR_MESSAGE
  }

  // 6. The backend gave a code we could not map, and it carried no message. Do NOT
  //    fall through to raw HTTP status text — the student would see "Unprocessable
  //    Entity" or "Not Found". Two live codes hit this: auto_enroll_active (422,
  //    access code entered on an auto-enroll course) and canvas_course_not_found
  //    (404, TWA-hosted course deleted in Canvas). Before step 1 became
  //    map-aware, any code returned the generic message; keep that floor.
  if (errorCode) {
    return DEFAULT_ERROR_MESSAGE
  }

  // 7. No code at all — unchanged: HTTP status text, else generic.
  return error.response?.statusText || DEFAULT_ERROR_MESSAGE
}

/**
 * Extract error code from an API error response
 * @param {Error} error - Axios error object
 * @returns {string|null} Error code or null
 */
export function getErrorCode(error) {
  return error.response?.data?.error?.code || null
}

/**
 * Admin-facing variant of getErrorMessage: prefers the first entry of a
 * top-level `errors` array. The admin activation endpoints (activate_course,
 * activate_lti_course, update_consumer) reject with
 * { message: <generic label>, errors: [<specific reason>] } and no error
 * code, so getErrorMessage alone would surface only the generic label.
 *
 * @param {Error} error - Axios error object
 * @returns {string} Most specific available error message
 */
export function getDetailedErrorMessage(error) {
  const specific = error.response?.data?.errors?.[0]
  return typeof specific === 'string' && specific ? specific : getErrorMessage(error)
}
